import { useMemo, useEffect, useImperativeHandle, forwardRef } from "react";
import { z, ZodTypeAny } from "zod";
import { useForm } from "@core/features/form";
import { Form } from "@themes/pollinator/ui/Form";
import {
  type QuizQuestionSet as QuizQuestionSetType,
  makeQuestionSetFormFields,
  buildFormSchemaFromFields,
  filterFieldsByQuestionLogic,
} from "@core/features/quiz";
import { QuizFormField } from "@platform/features/programQuiz/components/QuizFormField";

export type QuizQuestionContentRef = {
  /** Trigger form submission from parent */
  submit: () => Promise<void>;
  /** Check if form is valid */
  isValid: boolean;
};

export type Props = {
  /** The current active question set to display */
  activeQuestionSet: QuizQuestionSetType;
  /** Filter function for questions in the set */
  filterQuestionSet: (
    question: QuizQuestionSetType["questions"][number],
    index: number
  ) => boolean;
  /** Handler for submitting the current question set */
  onSubmit: (data: z.infer<ZodTypeAny>) => Promise<void>;
  /** Form default values */
  formData: Record<string, unknown>;
  /** Current step index for animation key */
  stepIndex: number;
};

/**
 * Quiz question content component for FitQuizLayout.
 * Renders quiz questions without navigation controls - those are handled by the parent layout.
 * Exposes a ref for the parent to trigger form submission.
 */
export const QuizQuestionContent = forwardRef<QuizQuestionContentRef, Props>(
  function QuizQuestionContent(
    {
      activeQuestionSet,
      filterQuestionSet,
      onSubmit,
      formData,
      stepIndex,
    },
    ref
  ) {
    const allFields = useMemo(
      () =>
        makeQuestionSetFormFields(
          activeQuestionSet.questions.filter(filterQuestionSet)
        ),
      [activeQuestionSet, filterQuestionSet]
    );

    const schema = useMemo(
      () => buildFormSchemaFromFields(allFields),
      [allFields]
    );

    // Build default values with force_yes fields set to true
    const defaultValues = useMemo(() => {
      const forceYesDefaults: Record<string, unknown> = {};
      for (const field of allFields) {
        if (field.isForceYes) {
          forceYesDefaults[field.name] = true;
        }
      }
      return { ...forceYesDefaults, ...formData };
    }, [allFields, formData]);

    const { form, handleSubmit } = useForm({
      defaultValues,
      schema,
      onSubmit,
    });

    useEffect(() => {
      form.reset(defaultValues);
    }, [defaultValues, form]);

    const values = form.watch();

    const visibleFields = useMemo(() => {
      return filterFieldsByQuestionLogic(allFields, values);
    }, [allFields, values]);

    const { isValid } = form.formState;

    // Expose submit method and isValid state to parent
    useImperativeHandle(ref, () => ({
      submit: handleSubmit,
      isValid,
    }), [handleSubmit, isValid]);

    return (
      <Form
        key={stepIndex}
        form={form}
        className="w-full"
        innerClassName="w-full flex flex-col gap-8"
        errorContainerClassName="mb-4"
      >
        {visibleFields.map((field) => {
          const { labelVariant, ...fieldProps } = field;
          const safeVariant =
            labelVariant === "overline" ? "caption" : labelVariant;
          return (
            <QuizFormField
              key={field.name}
              {...fieldProps}
              labelVariant={safeVariant}
              className="gap-2"
            />
          );
        })}
      </Form>
    );
  }
);

export default QuizQuestionContent;
